import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';

export default function Settings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<{ share_id: string; share_enabled: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.getSettings()
      .then((res: any) => setSettings(res))
      .catch(() => alert('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleRegenerate = async () => {
    if (!confirm('Regenerate your share link? The old link will stop working.')) return;
    try {
      const res: any = await api.regenerateShare();
      setSettings((prev) => prev ? { ...prev, share_id: res.share_id } : null);
    } catch {
      alert('Failed to regenerate');
    }
  };

  const handleToggle = async () => {
    if (!settings) return;
    try {
      const res: any = await api.toggleShare(!settings.share_enabled);
      setSettings(res);
    } catch {
      alert('Failed to toggle sharing');
    }
  };

  const getShareUrl = () => {
    if (!settings?.share_id) return '';
    const origin = window.location.origin;
    return `${origin}/view/${settings.share_id}`;
  };

  const handleCopy = () => {
    const url = getShareUrl();
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const userId = localStorage.getItem('userId');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="px-4 pt-6 pb-4">
      <h1 className="text-2xl font-bold mb-6">Settings</h1>

      {/* User ID */}
      <div className="bg-slate-800 rounded-2xl p-4 border border-slate-700/50 mb-4">
        <h3 className="font-semibold text-sm mb-2">Your User ID</h3>
        <p className="text-xs text-slate-400 mb-3">Bookmark this page to return anytime.</p>
        <div className="bg-slate-900 rounded-xl p-3 flex items-center justify-between border border-slate-700">
          <code className="text-xs text-slate-300 break-all font-mono">{userId}</code>
          <button
            onClick={() => {
              if (userId) navigator.clipboard.writeText(userId);
            }}
            className="text-xs text-primary-400 hover:text-primary-300 ml-2 shrink-0"
          >
            Copy
          </button>
        </div>
      </div>

      {/* Sharing */}
      <div className="bg-slate-800 rounded-2xl p-4 border border-slate-700/50 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm">Accountability Coach</h3>
          <button
            onClick={handleToggle}
            className={`relative w-12 h-7 rounded-full transition-colors ${
              settings?.share_enabled ? 'bg-primary-600' : 'bg-slate-600'
            }`}
          >
            <div
              className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-transform ${
                settings?.share_enabled ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
        <p className="text-xs text-slate-400 mb-4">
          {settings?.share_enabled
            ? 'Anyone with this link can view your goals and progress.'
            : 'Sharing is disabled. Your coach link will not work.'}
        </p>

        {settings?.share_enabled && (
          <>
            <div className="bg-slate-900 rounded-xl p-3 flex items-center justify-between border border-slate-700 mb-3">
              <span className="text-xs text-slate-300 truncate mr-2">{getShareUrl()}</span>
              <button
                onClick={handleCopy}
                className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors shrink-0 ${
                  copied
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-primary-600 hover:bg-primary-500 text-white'
                }`}
              >
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
            <button
              onClick={handleRegenerate}
              className="text-xs text-slate-400 hover:text-white underline transition-colors"
            >
              Regenerate link
            </button>
          </>
        )}
      </div>

      {/* Danger Zone */}
      <div className="bg-slate-800 rounded-2xl p-4 border border-slate-700/50">
        <h3 className="font-semibold text-sm mb-3 text-red-400">Sign Out</h3>
        <p className="text-xs text-slate-400 mb-4">
          Your data is safe. You can sign back in with your User ID anytime.
        </p>
        <button
          onClick={() => {
            localStorage.removeItem('userId');
            navigate('/');
          }}
          className="w-full bg-slate-700 hover:bg-slate-600 text-white font-medium py-3 rounded-xl transition-colors text-sm"
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}
